// src/components/ValueCoherenceAlert.jsx
// Centre d'actions (ARTDAI §P2) — action « incohérence entre valeur assurée / expertise /
// retenue », rendue possible par la traçabilité des valeurs (§P3, artwork_valuation_history).
// Scopée à une collection : compare, par œuvre, la dernière valeur de chaque nature ; signale
// celles où l'écart max/min > seuil. Lecture seule (l'équipe/le propriétaire juge ensuite).
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";

const GAP_THRESHOLD = 1.4; // > 40 % d'écart entre deux natures = incohérence à vérifier
const COMPARE_TYPES = ["insurance", "expert", "retained"];

export default function ValueCoherenceAlert({ collectionId }) {
  const { t } = useTranslation();
  const [count, setCount] = useState(undefined);

  useEffect(() => {
    let alive = true;
    if (!collectionId) { setCount(0); return; }
    (async () => {
      try {
        const { data: aw } = await supabase.from("artworks").select("id").eq("collection_id", collectionId);
        const ids = (aw || []).map((a) => a.id).filter(Boolean);
        if (ids.length === 0) { if (alive) setCount(0); return; }
        const { data: rows } = await supabase
          .from("artwork_valuation_history")
          .select("artwork_id,value_type,amount_retained,amount_min,amount_max,valuation_date,created_at")
          .in("artwork_id", ids)
          .in("value_type", COMPARE_TYPES)
          .order("valuation_date", { ascending: false })
          .order("created_at", { ascending: false });
        // Dernière valeur par (œuvre, nature).
        const latest = {};
        (rows || []).forEach((r) => {
          const k = r.artwork_id + "|" + r.value_type;
          if (!latest[k]) latest[k] = r;
        });
        const byArt = {};
        Object.values(latest).forEach((r) => {
          const amt = Number(r.amount_retained ?? r.amount_min ?? r.amount_max);
          if (!Number.isFinite(amt) || amt <= 0) return;
          (byArt[r.artwork_id] = byArt[r.artwork_id] || []).push(amt);
        });
        let n = 0;
        Object.values(byArt).forEach((amts) => {
          if (amts.length < 2) return;
          const max = Math.max(...amts), min = Math.min(...amts);
          if (min > 0 && max / min > GAP_THRESHOLD) n++;
        });
        if (alive) setCount(n);
      } catch {
        if (alive) setCount(0);
      }
    })();
    return () => { alive = false; };
  }, [collectionId]);

  if (!count) return null;

  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 10,
        border: "1px solid #e7c4c4", background: "#fbecec", color: "#8a2b2b",
        borderRadius: 12, padding: "12px 16px", marginBottom: 24, fontSize: 13.5,
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: 9999, background: "#b3261e", flexShrink: 0 }} />
      <span>
        {t("valueCoherence.alert", {
          defaultValue: "{{count}} œuvre(s) présentent un écart notable entre valeur assurée, d'expertise et retenue — à vérifier.",
          count,
        })}
      </span>
    </div>
  );
}
